import {apiFetchRaw} from "@/lib/api";

export {
   DICTATION_MAX_SESSION_MS,
   DICTATION_MAX_SESSION_SECONDS,
   DICTATION_TEMP_KEY_TTL_SECONDS,
   appendDictationTranscript,
   composeLiveDictationValue,
   mergeDictationTranscript,
   splitDictationTokens,
   type DictationToken,
} from "@repo/ui/gailgpt/dictation";

export function isDictationSupported(): boolean {
   if (typeof window === "undefined") {
      return false;
   }
   return (
      typeof WebSocket !== "undefined" &&
      typeof MediaRecorder !== "undefined" &&
      !!navigator.mediaDevices?.getUserMedia
   );
}

export function isMicrophonePermissionDenied(error: unknown): boolean {
   return (
      error instanceof DOMException &&
      (error.name === "NotAllowedError" || error.name === "PermissionDeniedError")
   );
}

/** Stop an in-progress session when the tab hides or the page unloads. */
export function subscribeDictationVisibilityStop(stop: () => void): () => void {
   const stopIfHidden = () => {
      if (document.visibilityState === "hidden") {
         stop();
      }
   };
   document.addEventListener("visibilitychange", stopIfHidden);
   window.addEventListener("pagehide", stop);
   return () => {
      document.removeEventListener("visibilitychange", stopIfHidden);
      window.removeEventListener("pagehide", stop);
   };
}

export function dictationErrorMessage(
   t: (key: string) => string,
   code: string | undefined,
   status: number
): string {
   if (status === 429 || code === "rate_limited") {
      return t("dictation.rateLimited");
   }
   return t("dictation.failed");
}

export type DictateSession = {
   apiKey: string;
   wsUrl: string;
   model: string;
   languageHints: string[];
};

export type DictateSessionResult =
   | {ok: true; session: DictateSession}
   | {ok: false; status: number; code?: string; aborted?: boolean};

function parseDictateSession(payload: unknown): DictateSession | null {
   if (!payload || typeof payload !== "object") {
      return null;
   }
   const body = payload as Record<string, unknown>;
   const apiKey = typeof body.apiKey === "string" ? body.apiKey.trim() : "";
   const wsUrl = typeof body.wsUrl === "string" ? body.wsUrl.trim() : "";
   const model = typeof body.model === "string" ? body.model.trim() : "";
   const languageHints = Array.isArray(body.languageHints)
      ? body.languageHints.filter((hint): hint is string => typeof hint === "string" && hint.length > 0)
      : [];
   if (!apiKey || !wsUrl || !model) {
      return null;
   }
   return {apiKey, wsUrl, model, languageHints};
}

/** Log Soniox session duration after the user stops talking. Never throws. */
export async function reportDictationUsage(input: {
   durationMs: number;
   characters: number;
}): Promise<void> {
   try {
      await apiFetchRaw("/api/gailgpt/dictate/usage", "POST", {
         body: {
            durationMs: input.durationMs,
            characters: input.characters,
         },
      });
   } catch {
      // Tracking must not fail dictation.
   }
}

/** Mint a single-use Soniox temp key for one dictation session. */
export async function requestDictationSession(signal: AbortSignal): Promise<DictateSessionResult> {
   try {
      const response = await apiFetchRaw("/api/gailgpt/dictate", "POST", {signal});
      if (signal.aborted) {
         return {ok: false, status: 0, aborted: true};
      }
      if (!response.ok) {
         let code: string | undefined;
         try {
            const payload = (await response.json()) as {error?: string};
            code = payload.error;
         } catch {
            code = undefined;
         }
         return {ok: false, status: response.status, code};
      }
      const session = parseDictateSession(await response.json());
      if (!session) {
         return {ok: false, status: 500, code: "transcription_failed"};
      }
      return {ok: true, session};
   } catch (error) {
      if ((error instanceof DOMException && error.name === "AbortError") || signal.aborted) {
         return {ok: false, status: 0, aborted: true};
      }
      throw error;
   }
}
